import {
  alpha,
  Box,
  Chip,
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import React from 'react';

const StyledSectionTotalTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.blue.main,
    color: theme.palette.common.white,
    borderBottom: 'none'
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 12,
    borderBottom: 'none'
  }
}));

const TotalsCard = ({ team, focusTeamId = null, focusToken = 0 }) => {
  const focusRowRef = React.useRef(null);

  // A non-sectioned log has no player rows, so only the team row is located.
  React.useEffect(() => {
    if (!focusTeamId || !focusToken) {
      return undefined;
    }

    const prefersReducedMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const frame = window.requestAnimationFrame(() => {
      focusRowRef.current?.scrollIntoView({
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
        block: 'center',
        inline: 'nearest'
      });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [focusTeamId, focusToken]);

  if (team.totals && team.totals.teams.length !== 0) {
    let teamTotals = team.totals.teams;
    const mutableTeamTotals = Array.from(teamTotals);

    mutableTeamTotals.sort((a, b) => {
      if (b.totalPoints !== a.totalPoints) {
        return b.totalPoints - a.totalPoints;
      }

      return b.totalNet - a.totalNet;
    });

    teamTotals = mutableTeamTotals;

    return (
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 3,
          maxWidth: 500,
          border: '1px solid',
          borderColor: 'blue.main'
        }}>
        <Table padding="none">
          <TableHead>
            <TableRow>
              <StyledSectionTotalTableCell padding="normal">
                <Typography variant="body1" fontWeight={'bold'}>
                  Totals
                </Typography>
              </StyledSectionTotalTableCell>
              <StyledSectionTotalTableCell padding="normal" align={'center'}>
                Games
              </StyledSectionTotalTableCell>
              <StyledSectionTotalTableCell padding="normal" align={'center'}>
                Total
              </StyledSectionTotalTableCell>
              <StyledSectionTotalTableCell padding="normal" align={'center'}>
                Net
              </StyledSectionTotalTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {teamTotals.map((total) => {
              const isFocusedTeam =
                Boolean(focusTeamId) && focusTeamId === total.teamId;

              return (
                <TableRow
                  key={total.teamId}
                  ref={isFocusedTeam ? focusRowRef : undefined}
                  sx={
                    isFocusedTeam
                      ? {
                          backgroundColor: (activeTheme) =>
                            alpha(activeTheme.palette.warning.main, 0.18)
                        }
                      : undefined
                  }>
                  <StyledSectionTotalTableCell size="small" padding="normal">
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      {total.teamName}
                      {isFocusedTeam && (
                        <Chip size="small" label="You" color="warning" />
                      )}
                    </Box>
                  </StyledSectionTotalTableCell>
                  <StyledSectionTotalTableCell size="small" align={'center'}>
                    {total.totalGames}
                  </StyledSectionTotalTableCell>
                  <StyledSectionTotalTableCell size="small" align={'center'}>
                    {total.totalPoints}
                  </StyledSectionTotalTableCell>
                  <StyledSectionTotalTableCell size="small" align={'center'}>
                    {total.totalNet}
                  </StyledSectionTotalTableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  } else {
    return (
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 3,
          maxWidth: 400,
          border: '1px solid',
          borderColor: 'blue.main'
        }}>
        <Table padding="none">
          <TableHead>
            <TableRow>
              <StyledSectionTotalTableCell padding="normal">
                <Typography variant="body1" fontWeight={'bold'}>
                  Totals
                </Typography>
              </StyledSectionTotalTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <StyledSectionTotalTableCell size="small" padding="normal">
                No data available yet, check back again later.
              </StyledSectionTotalTableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  }
};

export default TotalsCard;
